import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Card, Alert, Spinner, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaUpload, FaCamera, FaImage } from 'react-icons/fa';
import { useUserAuth } from '../../context/UserAuthContext';
import api from '../../services/api';
// heic2any is dynamically imported when needed to reduce bundle size
import './Photos.css';

const PhotoUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadDisabled, setUploadDisabled] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useUserAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Add state for app settings with better defaults to prevent loading delays
  const [settings, setSettings] = useState({
    maxPhotoSizeMB: 20, // Default to 20MB to match server default
    allowPhotoUploads: true
  });
  const [loadingSettings, setLoadingSettings] = useState(false); // Start with false to show UI immediately

  // Add a state to track if we're dealing with a HEIC file
  const [isHeicFile, setIsHeicFile] = useState(false);
  const [convertingHeic, setConvertingHeic] = useState(false);
  
  // Fetch application settings when component mounts (non-blocking)
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
        
        const response = await api.get('/settings', {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.data && response.data.settings) {
          setSettings(prev => ({
            ...prev,
            ...response.data.settings
          }));
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          console.warn('Settings fetch timed out, using defaults');
        } else {
          console.error('Error fetching settings:', err);
        }
        // Keep using default values if settings fetch fails
      }
    };
    
    // Use setTimeout to defer settings fetch and not block initial render
    const timeoutId = setTimeout(fetchSettings, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Function to convert HEIC to JPEG with fallback options
  const convertHeicFile = async (file) => {
    try {
      setConvertingHeic(true);
      
      try {
        // Dynamically import heic2any only when needed
        const { default: heic2any } = await import('heic2any');
        
        // First try client-side conversion using heic2any
        const jpegBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.8
        });
        
        // Create a new File object with the converted JPEG data
        const convertedFile = new File(
          [jpegBlob],
          file.name.replace(/\.heic$/i, '.jpg'),
          { type: 'image/jpeg' }
        );
        
        // Update the selected file to the converted JPEG for upload
        setSelectedFile(convertedFile);
        
        // Create a preview URL from the JPEG blob
        const reader = new FileReader();
        reader.onload = () => {
          setPreviewUrl(reader.result);
          setConvertingHeic(false);
          // Reset the HEIC flag since we now have a JPEG preview
          setIsHeicFile(false);
        };
        reader.onerror = () => {
          throw new Error('Failed to read converted blob');
        };
        reader.readAsDataURL(jpegBlob);
        
        return; // Success - early return
      } catch (clientError) {
        console.warn('Client-side HEIC conversion failed, falling back to server:', clientError);
        // Continue to server-side fallback
      }
      
      // Server-side fallback conversion
      const formData = new FormData();
      formData.append('photo', file);
      
      const response = await api.post('/photos/convert', formData, {
        responseType: 'blob'
      });
      
      // Create a File object from the response blob
      const convertedFile = new File(
        [response.data],
        file.name.replace(/\.heic$/i, '.jpg'),
        { type: 'image/jpeg' }
      );
      
      // Update selected file to the converted one
      setSelectedFile(convertedFile);
      
      // Create preview from the converted file
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
        setConvertingHeic(false);
        setIsHeicFile(false);
      };
      reader.onerror = () => {
        throw new Error('Failed to read server-converted blob');
      };
      reader.readAsDataURL(response.data);
      
    } catch (error) {
      console.error('All HEIC conversion methods failed:', error);
      // Don't show error message if it's just the preview that failed
      // but still allow upload with the original file
      setConvertingHeic(false);
      setIsHeicFile(true);
      
      // Try to create a preview from the original HEIC file
      // This might not work on all browsers but at least we try
      try {
        const reader = new FileReader();
        reader.onload = () => {
          setPreviewUrl(reader.result);
        };
        reader.onerror = () => {
          console.warn('Failed to create preview from HEIC file');
          // Show a placeholder or generic image icon
          setPreviewUrl('');
        };
        reader.readAsDataURL(file);
      } catch (previewError) {
        console.warn('Failed to create HEIC preview:', previewError);
        setPreviewUrl('');
      }
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type - support HEIC files too
    const isHEIC = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
    if (!file.type.startsWith('image/') && !isHEIC) {
      setError(t('photoUpload.errorInvalidType'));
      return;
    }

    // Check file size against current setting
    const maxSizeBytes = settings.maxPhotoSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setError(`${t('photoUpload.errorFileSize')} (${settings.maxPhotoSizeMB}MB)`);
      return;
    }

    setError('');
    setSelectedFile(file);
    
    // For HEIC files, try to convert them on the server
    if (isHEIC) {
      // Reset the preview
      setPreviewUrl('');
      // Initially set the HEIC flag
      setIsHeicFile(true);
      // Try to convert
      await convertHeicFile(file);
    } else {
      // Reset HEIC flag
      setIsHeicFile(false);
      setConvertingHeic(false);
      
      // Regular image preview
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.onerror = () => {
        setError(t('photoUpload.errorPreviewFailed'));
      };
      reader.readAsDataURL(file);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setError(t('photoUpload.errorNoFile'));
      return;
    }

    if (!user) {
      setError(t('photoUpload.errorNotLoggedIn'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Sending photo upload request', {
        photoSize: selectedFile.size,
        photoType: selectedFile.type,
        fileName: selectedFile.name,
        description: description ? 'Provided' : 'None'
      });

      let response;

      // Check if we're in localhost or production
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isLocalhost) {
        // Use FormData for localhost (works with local backend)
        const formData = new FormData();
        formData.append('photo', selectedFile);
        if (description) {
          formData.append('description', description);
        }
        response = await api.post('/photos', formData);
      } else {
        // Use Base64 for production (Firebase Functions compatible)
        const reader = new FileReader();
        const base64Promise = new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(selectedFile);
        });
        
        const photoData = await base64Promise;
        
        const uploadData = {
          photoData,
          fileName: selectedFile.name,
          mimeType: selectedFile.type,
          description: description || ''
        };
        
        response = await api.post('/photos/upload-base64', uploadData);
      }
      
      console.log('Upload successful:', response.data);

      // Disable upload button after successful upload to prevent double submissions
      setUploadDisabled(true);
      
      // Reset HEIC state
      setIsHeicFile(false);
      
      // Redirect to gallery
      setTimeout(() => {
        navigate('/photos');
      }, 1500);
      
    } catch (err) {
      console.error('Upload error:', {
        message: err.message,
        response: err.response ? {
          status: err.response.status,
          data: err.response.data
        } : 'No response',
        request: err.request ? 'Request made but no response' : 'No request made'
      });
      
      if (err.response) {
        const status = err.response.status;
        const errorData = err.response.data;
        
        switch (status) {
          case 403:
            setError(t('photoUpload.errorUploadsDisabled'));
            break;
          case 401:
            setError('Authentication failed. Please log out and log back in.');
            break;
          case 400:
            if (errorData && errorData.message) {
              if (errorData.message.includes('File too large') || 
                  errorData.message.includes('Maximum size') ||
                  errorData.message.includes('exceeds')) {
                setError(`File size error: ${errorData.message}`);
              } else if (errorData.message.includes('Invalid') || 
                        errorData.message.includes('type')) {
                setError(`File type error: ${errorData.message}`);
              } else {
                setError(errorData.message);
              }
            } else {
              setError('Invalid request. Please check your file and try again.');
            }
            break;
          case 500:
            if (errorData && errorData.message) {
              if (errorData.message.includes('Storage') || 
                  errorData.message.includes('bucket')) {
                setError('Storage service error. Please try again in a few minutes.');
              } else if (errorData.message.includes('Database') || 
                        errorData.message.includes('Firestore')) {
                setError('Database error. Please try again.');
              } else {
                setError(`Server error: ${errorData.message}`);
              }
            } else {
              setError('Server error. Please try again later.');
            }
            break;
          default:
            setError(`Upload failed (${status}): ${errorData?.message || 'Unknown error'}`);
        }
      } else if (err.request) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(`Upload error: ${err.message || 'Unknown error occurred'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  return (
    <Container className="photo-upload-container py-5">
      <div className="text-center mb-4">
        <h2>{t('photoUpload.title')}</h2>
        <p className="text-muted">{t('photoUpload.subtitle')}</p>
      </div>

      <Card className="mx-auto upload-card">
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            <div className="mb-4">
              <div 
                className={`upload-area ${previewUrl ? 'has-preview' : ''}`}
                onClick={triggerFileInput}
              >
                {previewUrl ? (
                  <Image 
                    src={previewUrl} 
                    alt="Preview" 
                    className="img-preview" 
                  />
                ) : convertingHeic ? (
                  <div className="upload-placeholder">
                    <Spinner
                      animation="border"
                      role="status"
                      className="mb-3"
                    />
                    <p>{t('photoUpload.convertingHeic')}</p>
                    <small className="text-muted">{t('photoUpload.pleaseWait')}</small>
                  </div>
                ) : isHeicFile && selectedFile ? (
                  <div className="upload-placeholder heic-placeholder">
                    <FaImage className="upload-icon" />
                    <p>{t('photoUpload.heicSelected')}</p>
                    <small className="text-muted">{t('photoUpload.previewProcessed')}</small>
                    <small className="text-muted d-block mt-1">{t('photoUpload.heicConvertNotice')}</small>
                    <small className="text-success d-block mt-1">
                      HEIC images will be automatically converted to JPEG for compatibility
                    </small>
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      className="mt-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        setIsHeicFile(false);
                      }}
                    >
                      {t('photoUpload.changeImage')}
                    </Button>
                  </div>
                ) : loading ? (
                  <div className="upload-placeholder">
                    <Spinner
                      animation="border"
                      role="status"
                      className="mb-3"
                    />
                    <p>{t('photoUpload.uploading')}</p>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <FaCamera className="upload-icon" />
                    <p>{t('photoUpload.dragDropPrompt')}</p>
                    <small className="text-muted">
                      {`${t('photoUpload.maxFileSize')}: ${settings.maxPhotoSizeMB}MB`}
                    </small>
                    <small className="text-muted d-block mt-1">HEIC images from iPhone will be converted to JPEG automatically</small>
                  </div>
                )}
              </div>
              <Form.Control
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*,.heic,.heif"
                className="d-none"
              />
              <Button 
                variant="outline-primary" 
                onClick={triggerFileInput} 
                className="w-100 mt-2"
              >
                {t('photoUpload.selectButton')}
              </Button>
            </div>

            <Form.Group className="mb-4">
              <Form.Label>{t('photoUpload.descriptionLabel')}</Form.Label>
              <Form.Control
                as="textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('photoUpload.descriptionPlaceholder')}
                rows={3}
                maxLength={200}
              />
              <Form.Text className="text-muted">
                {description.length}/200 {t('photoUpload.charactersRemaining')}
              </Form.Text>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit" 
                className="upload-submit-btn"
                disabled={loading || !selectedFile || uploadDisabled}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {t('photoUpload.uploading')}
                  </>
                ) : (
                  <>
                    <FaUpload className="me-2" />
                    {t('photoUpload.uploadButton')}
                  </>
                )}
              </Button>
              <Button 
                variant="outline-secondary" 
                onClick={() => navigate('/photos')}
                disabled={loading}
              >
                {t('cancel')}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PhotoUpload;